import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronDown, ChevronUp, Bot, Trash2 } from 'lucide-react';
import DefaultQuestions from './DefaultQuestions';
import ChatInterface from './ChatInterface';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TradingAssistantModal = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [questionsCollapsed, setQuestionsCollapsed] = useState(false);
  const [defaultQuestions, setDefaultQuestions] = useState(null);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isAvailable, setIsAvailable] = useState(true);

  // Check health and fetch default questions on mount
  useEffect(() => {
    if (isOpen) {
      checkHealth();
      fetchDefaultQuestions();
      
      // Add welcome message if no messages
      if (messages.length === 0) {
        setMessages([{
          role: 'assistant',
          content: "👋 **Welcome to the Trading Assistant!**\n\nI can help you:\n\n📊 **Query your signal performance** - Win rates, P&L, coin breakdown\n\n🛡️ **Check guard activity** - See which guards are blocking signals\n\n📈 **Execute paper trades** - Buy/sell stocks and crypto on Alpaca\n\n💼 **Manage your portfolio** - View positions, orders, account balance\n\nClick a question below or type your own!"
        }]);
      }
    }
  }, [isOpen]);

  const checkHealth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trading-assistant/health`);
      const data = await response.json();
      setIsAvailable(data.available);
    } catch (error) {
      console.error('Health check failed:', error);
      setIsAvailable(false);
    }
  };

  const fetchDefaultQuestions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trading-assistant/default-questions`);
      const data = await response.json();
      setDefaultQuestions(data.categories);
    } catch (error) {
      console.error('Failed to fetch default questions:', error);
    }
  };

  const sendMessage = useCallback(async (message) => {
    if (!message.trim() || isLoading) return;

    // Add user message
    const userMessage = { role: 'user', content: message };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Collapse questions after first message
    if (!questionsCollapsed) {
      setQuestionsCollapsed(true);
    }

    try {
      const response = await fetch(`${API_URL}/api/trading-assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: message
        }),
      });

      const data = await response.json();
      
      // Add assistant response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        tools_used: data.tools_used
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isLoading, questionsCollapsed]);

  const handleQuestionClick = (question) => {
    sendMessage(question);
  };

  const clearChat = async () => {
    try {
      await fetch(`${API_URL}/api/trading-assistant/clear/${sessionId}`, {
        method: 'DELETE',
      });
      setMessages([{
        role: 'assistant',
        content: "👋 Chat cleared! How can I help you today?"
      }]);
      setQuestionsCollapsed(false);
    } catch (error) {
      console.error('Failed to clear chat:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div 
        className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] max-h-[900px] flex flex-col
                   border border-gray-700 overflow-hidden"
        data-testid="trading-assistant-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gray-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Trading Assistant</h2>
              <p className="text-xs text-gray-400">
                {isAvailable ? 'Powered by Claude AI' : 'Service unavailable'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={clearChat}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              title="Clear chat"
              data-testid="clear-chat-button"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              data-testid="close-modal-button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden p-4 min-h-0">
          {/* Toggle Questions Button */}
          {defaultQuestions && (
            <button
              onClick={() => setQuestionsCollapsed(!questionsCollapsed)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-3 transition-colors flex-shrink-0"
            >
              {questionsCollapsed ? (
                <>
                  <ChevronDown className="w-4 h-4" />
                  <span>Show suggested questions</span>
                </>
              ) : (
                <>
                  <ChevronUp className="w-4 h-4" />
                  <span>Hide suggested questions</span>
                </>
              )}
            </button>
          )}

          {/* Default Questions - scrollable if needed */}
          {defaultQuestions && !questionsCollapsed && (
            <div className="flex-shrink-0 max-h-[280px] overflow-y-auto mb-4">
              <DefaultQuestions
                categories={defaultQuestions}
                onQuestionClick={handleQuestionClick}
                collapsed={questionsCollapsed}
              />
            </div>
          )}

          {/* Chat Interface - takes remaining space */}
          <div className="flex-1 min-h-[200px] overflow-hidden flex flex-col">
            <ChatInterface
              messages={messages}
              onSendMessage={sendMessage}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingAssistantModal;
